import axios from 'axios';
import { isPublicPath } from '../utils/nav';

/*
 * API client for the Listful backend.
 *
 * Base URL comes from `REACT_APP_API_URL` in `.env` (or `.env.local` for overrides).
 * Default fallback is the local Laravel dev server on port 8000 with the /api/v1
 * prefix.  In CI or production you'll set `REACT_APP_API_URL` at build time.
 *
 * NOTE on env vars: this project uses Create React App, so env vars must be
 * prefixed with REACT_APP_ and read via process.env.  (Vite-style
 * import.meta.env.VITE_* does not work here.)  CRA only reloads .env on
 * dev-server restart — `npm start` again after changing it.
 *
 * The backend wraps every response in an envelope:
 *   { success: true, data: ..., message: "", environment: "local" }
 *
 * To avoid touching every component (which all read `response.data` directly),
 * we unwrap the envelope here.  After the interceptor runs:
 *   - response.data        === the actual payload (what the controller returned)
 *   - response.environment === "local" | "staging" | "production" | "testing"
 *   - response.success     === true (it's always true here because errors get thrown)
 *
 * On a failure response, the rejection's `error.response.data` follows the
 * error envelope shape:
 *   { success: false, error: "...", errors: {...}, code: 422, environment: "..." }
 */

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8008/api/v1',
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    // We use Bearer tokens, not cookies.  withCredentials only matters for
    // cookie-based session auth.  Leaving it off avoids unnecessary CORS
    // complications.
    withCredentials: false,
});

// Attach the auth token to every outgoing request, if we have one.
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Unwrap the success envelope, and bubble useful fields onto the response.
api.interceptors.response.use(
    (response) => {
        const body = response.data;
        if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
            response.environment = body.environment;
            response.message = body.message;
            response.success = body.success;
            response.data = body.data;
        }
        return response;
    },
    (error) => {
        // A 401 always means the stored session is dead, so always clear it.
        //
        // Where we go next depends on the page. On a PROTECTED page there is nothing
        // to show once the session is gone, so bounce to /login. On a PUBLIC page —
        // the landing page, the legal pages, an invite link — do NOT navigate: clear
        // the dead session and let the page render.
        //
        // This mattered more than it looks. A visitor with an expired token who opened
        // gifts.memula.com was thrown to /login by this handler before the landing page
        // could render, so the one audience the landing page exists for — someone who
        // tried the app once, months ago, and has just followed an invite link — was
        // exactly the audience that never saw it. The hard `window.location.href` also
        // bypasses react-router entirely, so the SPA never gets a say.
        if (error.response?.status === 401) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');

            if (!isPublicPath(window.location.pathname)) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export default api;
